import React, { useEffect, useState } from 'react';
import api from '../api';
import Avatar from '../components/Avatar';
import useCoachAvatars from '../hooks/useCoachAvatars';

// 'declined' sits after 'submitted' because that is the order the work
// happens in: what is waiting, what was queried, then what has moved on.
const TABS = ['submitted', 'declined', 'approved', 'billed', 'paid'];
const TAB_LABELS = {
  submitted: 'Waiting', declined: 'Sent back', approved: 'Approved',
  billed: 'Invoiced', paid: 'Paid'
};

const Submissions = () => {
  const [tab, setTab] = useState('submitted');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  // Which row is being edited or queried, and what has been typed into it.
  // One at a time on purpose: an admin correcting six rows at once and losing
  // track of which is which is how the wrong coach gets paid the wrong figure.
  const [editing, setEditing] = useState(null);   // { id, hours }
  const [sendingBack, setSendingBack] = useState(null); // { id, reason }
  const [busy, setBusy] = useState(false);
  // Fetched once for the page, not once per row. The same coaches repeat
  // across hundreds of rows and that only grows.
  const avatars = useCoachAvatars();

  const load = (status) => api.get(`/api/submissions?status=${status}`).then(res => { setItems(res.data); setSelected([]); });
  useEffect(() => { load(tab); }, [tab]);

  const toggle = (id) => setSelected(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  const toggleAll = () => setSelected(sel => sel.length === items.length ? [] : items.map(i => i.id));

  const run = async (fn, done) => {
    setBusy(true); setError(''); setSuccess('');
    try {
      await fn();
      setSuccess(done);
      setEditing(null);
      setSendingBack(null);
      load(tab);
    } catch (err) {
      setError(err.response?.data?.error || 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    if (!selected.length) return;
    let n = 0;
    await run(async () => {
      const res = await api.post('/api/submissions/approve', { ids: selected });
      n = res.data.approved;
    }, '');
    setSuccess(`${n} session${n === 1 ? '' : 's'} approved.`);
  };

  const unapprove = (s) => run(
    () => api.post(`/api/submissions/${s.id}/unapprove`),
    `${s.coach_name}'s session is back with the ones waiting to be approved.`
  );

  const sendBack = (s) => run(
    () => api.post(`/api/submissions/${s.id}/decline`, { reason: sendingBack.reason }),
    `Sent back to ${s.coach_name}. They will see your reason and can put it right.`
  );

  const saveHours = (s) => run(
    () => api.patch(`/api/submissions/${s.id}`, { hours: editing.hours }),
    `${s.coach_name}'s hours changed. They can see it was you who changed them.`
  );

  // A DATE column arrives as 'YYYY-MM-DD'. Build it from its parts so the
  // browser's timezone cannot shift it onto the day before.
  const fmtDate = (d) => {
    if (!d) return '—';
    const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
    if (!y || !m || !day) return String(d);
    return new Date(y, m - 1, day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // created_at is a real timestamp, so the browser can parse it directly.
  const fmtLogged = (t) => (t ? new Date(t).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) : '—');

  const describe = (s) =>
    s.session_type === 'away_game'
      ? `Away @ ${s.venue_name || 'venue not set'}`
      : (s.session_label || 'Session');

  return (
    <>
      <h1>Coach submissions</h1>
      {success && <div className="success-msg">{success}</div>}
      {error && <div className="error-msg prewrap">{error}</div>}
      <div className="tabs">
        {TABS.map(t => (
          <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>
            {TAB_LABELS[t] || t}
          </button>
        ))}
      </div>

      <div className="card">
        {tab === 'submitted' && items.length > 0 && (
          <div style={{ marginBottom: '0.75rem' }}>
            <button className="btn orange" disabled={!selected.length} onClick={approve}>
              Approve selected ({selected.length})
            </button>
          </div>
        )}
        {items.length === 0 ? <p className="muted">Nothing here.</p> : (
          <table>
            <thead>
              <tr>
                {tab === 'submitted' && <th><input type="checkbox" checked={selected.length === items.length} onChange={toggleAll} /></th>}
                <th>Coach</th><th>Session date</th><th>Logged on</th><th>Session</th><th>Hours</th><th>Mileage</th><th>Total</th><th></th>
              </tr>
            </thead>
            <tbody>
              {items.flatMap(s => ([
                <tr key={s.id}>
                  {tab === 'submitted' && <td><input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} /></td>}
                  <td>
                    <span className="cell-with-avatar">
                      <Avatar name={s.coach_name} src={avatars[s.coach_id]} size="sm" />
                      {s.coach_name}
                    </span>
                  </td>
                  <td>{fmtDate(s.session_date)}</td>
                  <td className="muted">{fmtLogged(s.created_at)}</td>
                  <td>{describe(s)}</td>
                  <td>
                    {editing && editing.id === s.id ? (
                      <input
                        className="hours-input"
                        type="number"
                        step="0.25"
                        min="0.25"
                        max="24"
                        value={editing.hours}
                        autoFocus
                        onChange={e => setEditing({ ...editing, hours: e.target.value })}
                      />
                    ) : s.hours}
                    {/* Says the figure was changed and by whom. A coach's pay
                        being altered should never be something they have to
                        notice for themselves. */}
                    {s.amended_at && (
                      <div className="muted amended-note">
                        was {Number(s.amended_from_hours)}, changed by {s.amended_by_name}
                      </div>
                    )}
                  </td>
                  <td>{s.miles > 0 ? `${s.miles} mi / £${Number(s.mileage_amount).toFixed(2)}` : '—'}</td>
                  <td>£{Number(s.line_total).toFixed(2)}</td>
                  <td>
                    <div className="row-actions">
                      {editing && editing.id === s.id ? (
                        <>
                          <button className="btn small" disabled={busy} onClick={() => saveHours(s)}>Save</button>
                          <button className="btn small secondary" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          {/* Nothing is offered on a paid invoice. The server
                              refuses it anyway, but a button that always fails
                              is worse than no button. */}
                          {tab !== 'paid' && (
                            <button className="btn small secondary" disabled={busy}
                              onClick={() => { setSendingBack(null); setEditing({ id: s.id, hours: s.hours }); }}>
                              Change hours
                            </button>
                          )}
                          {tab === 'approved' && (
                            <button className="btn small secondary" disabled={busy} onClick={() => unapprove(s)}>
                              Un-approve
                            </button>
                          )}
                          {tab !== 'paid' && tab !== 'declined' && (
                            <button className="btn small secondary" disabled={busy}
                              onClick={() => { setEditing(null); setSendingBack({ id: s.id, reason: '' }); }}>
                              Send back
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>,
                sendingBack && sendingBack.id === s.id ? (
                  <tr key={`${s.id}-back`} className="sendback-row">
                    <td colSpan={tab === 'submitted' ? 9 : 8}>
                      <label htmlFor={`reason-${s.id}`}>
                        Why are you sending this back to {s.coach_name}?
                      </label>
                      <div className="sendback-controls">
                        <input
                          id={`reason-${s.id}`}
                          value={sendingBack.reason}
                          autoFocus
                          placeholder="e.g. that session was cancelled, or the hours look high"
                          onChange={e => setSendingBack({ ...sendingBack, reason: e.target.value })}
                        />
                        <button className="btn small" disabled={busy || !sendingBack.reason.trim()} onClick={() => sendBack(s)}>
                          Send it back
                        </button>
                        <button className="btn small secondary" disabled={busy} onClick={() => setSendingBack(null)}>
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : null,
                s.declined_reason ? (
                  <tr key={`${s.id}-reason`} className="sendback-row">
                    <td colSpan={tab === 'submitted' ? 9 : 8}>
                      <span className="muted">Sent back: {s.declined_reason}</span>
                    </td>
                  </tr>
                ) : null
              ]))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default Submissions;
